// Data & Retention settings tab — the user's default artifact retention window. This value is stamped
// on NEWLY-published artifact versions (Settings drives what the backend used to hardcode). Existing
// artifacts keep their own retention. The number is clamped to [1, MAX] on save.

import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import InputAdornment from '@mui/material/InputAdornment'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import type { UserSettings, UserSettingsPatch } from '../pages/userSettingsApi'
import { SERIF_HEADING } from '../theme'

// Mirror the backend policy (reachy-cf/src/lib/artifact-limits.ts). Kept in sync by hand — small,
// stable constants; the server still clamps authoritatively.
const DEFAULT_RETENTION_DAYS = 180
const MAX_RETENTION_DAYS = 365

export function SettingsDataRetention({
  settings, onSave,
}: {
  settings: UserSettings | null
  onSave: (patch: UserSettingsPatch) => Promise<UserSettings>
}) {
  const effective = settings?.retentionDays ?? DEFAULT_RETENTION_DAYS
  const [value, setValue] = useState<string>(String(effective))
  // Persistent green/red header status (same pattern as the General tab) — not a fleeting toast.
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; msg: string } | null>(null)

  // Re-seed when settings load / change underneath.
  useEffect(() => { setValue(String(settings?.retentionDays ?? DEFAULT_RETENTION_DAYS)) }, [settings?.retentionDays])

  const commit = async () => {
    const n = Math.min(MAX_RETENTION_DAYS, Math.max(1, Math.floor(Number(value) || DEFAULT_RETENTION_DAYS)))
    setValue(String(n))
    if (n === effective) return
    try { await onSave({ retentionDays: n }); setStatus({ kind: 'ok', msg: 'Saved' }) }
    catch { setStatus({ kind: 'error', msg: 'Could not save — try again.' }); setValue(String(effective)) }
  }
  const resetDefault = async () => {
    setValue(String(DEFAULT_RETENTION_DAYS))
    try { await onSave({ retentionDays: null }); setStatus({ kind: 'ok', msg: 'Reset to default' }) }
    catch { setStatus({ kind: 'error', msg: 'Could not save — try again.' }) }
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: 5, py: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
        <Typography variant="h5" sx={SERIF_HEADING}>Data & Retention</Typography>
        {status && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.8rem', fontWeight: 600, color: status.kind === 'ok' ? 'success.main' : 'error.main' }}>
            {status.kind === 'ok' && <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            {status.msg}
          </Box>
        )}
      </Box>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        Control how long the artifacts Ros creates for you are kept before they expire.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3, py: 2.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flexShrink: 0, maxWidth: 360 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Default retention</Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
            Applied to new artifacts. You can still extend an individual artifact later, up to {MAX_RETENTION_DAYS} days.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
          <TextField
            size="small" type="number" value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={() => void commit()}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void commit() } }}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            inputProps={{ min: 1, max: MAX_RETENTION_DAYS, style: { width: 72 } }}
          />
          {(settings?.retentionDays ?? null) !== null && (
            <Button size="small" onClick={() => void resetDefault()} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
              Reset to default ({DEFAULT_RETENTION_DAYS} days)
            </Button>
          )}
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 2 }}>
        Between 1 and {MAX_RETENTION_DAYS} days.
      </Typography>
    </Box>
  )
}
